// §Batch 2d — Waitlist panel in App Detail (owner side). Lists the app's `waitlist` collection over
// GET /v1/apps/:appId/owner/waitlist (ownsApp-gated server-side; a non-owner gets an empty list,
// never a leak) and lets the owner move each entry through the waitlist lifecycle via
// PATCH /v1/apps/:appId/owner/waitlist/:id { status } (validated server-side against the lifecycle —
// an illegal transition is rejected, never a corrupt write). The waitlist is a SEPARATE collection,
// not a booking status; promoting a candidate here does NOT create a booking (that becomes real only
// once the reserve_slot reservation backend is wired). Honest loading / empty / error states.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { ClipboardList, RotateCw, Loader2 } from 'lucide-preact';
import { t } from '../lib/i18n';

interface WaitRow {
  id: string;
  status?: string;
  name?: string;
  contact?: string;
  requestedItem?: string;
  note?: string;
  createdAt?: string | number;
}

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: WaitRow[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' };

// The owner-facing labels + the allowed next transitions per current status (mirrors the
// WAITLIST_LIFECYCLE in status-lifecycle.ts — the server is the authority; this is just the UI).
// §S680 — SERVER status values map to i18n KEYS (resolved via t() at render);
// an unknown raw value renders as-is (honest fallback).
const STATUS_LABEL_KEY: Record<string, string> = {
  new: 'mgmt.wl.stNew',
  contacted: 'mgmt.wl.stContacted',
  promoted_candidate: 'mgmt.wl.stCandidate',
  dismissed: 'mgmt.wl.stDismissed',
};
const NEXT: Record<string, { to: string; labelKey: string }[]> = {
  new: [
    { to: 'contacted', labelKey: 'mgmt.wl.markContacted' },
    { to: 'promoted_candidate', labelKey: 'mgmt.wl.promote' },
    { to: 'dismissed', labelKey: 'mgmt.wl.dismiss' },
  ],
  contacted: [
    { to: 'promoted_candidate', labelKey: 'mgmt.wl.promote' },
    { to: 'dismissed', labelKey: 'mgmt.wl.dismiss' },
  ],
  promoted_candidate: [
    { to: 'contacted', labelKey: 'mgmt.wl.backToContacted' },
    { to: 'dismissed', labelKey: 'mgmt.wl.dismiss' },
  ],
  dismissed: [],
};

function whenLabel(ts?: string | number): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

export function WaitlistPanel({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' });
  const [busy, setBusy] = useState<string | null>(null); // id being mutated

  const fetchRows = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/waitlist`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: WaitRow[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const setStatus = useCallback(async (id: string, status: string) => {
    setBusy(id);
    try {
      const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/waitlist/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        const j = (await r.json().catch(() => ({}))) as { item?: WaitRow };
        const next = j.item?.status ?? status;
        setLoad((l) => (l.s === 'ready' ? { s: 'ready', items: l.items.map((it) => (it.id === id ? { ...it, status: next } : it)) } : l));
      }
    } catch { /* leave the row as-is on failure */ }
    finally { setBusy(null); }
  }, [appId]);

  return (
    <section aria-label={t('mgmt.wl.title')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ClipboardList size={16} /> {t('mgmt.wl.title')}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <button type="button" className="pf-btn pf-btn--sm" onClick={fetchRows} disabled={load.s === 'loading'}>
          <RotateCw size={13} /> {t('mgmt.refresh')}
        </button>
      </div>

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 56, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.wl.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.wl.unavailableS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.wl.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t('mgmt.wl.errAuth') : t('mgmt.wl.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchRows}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><ClipboardList size={22} /></span>
          <p className="pf-empty__title">{t('mgmt.wl.emptyT')}</p>
          <p className="pf-empty__lede">{t('mgmt.wl.emptyS')}</p>
        </div>
      )}

      {load.s === 'ready' && load.items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {load.items.map((e) => {
            const status = e.status ?? 'new';
            return (
              <li key={e.id} className="pf-mgmt-panel" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                <span className="pf-mgmt-panel__body">
                  <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {e.name || e.contact || t('mgmt.wl.fallback')}
                    <span className="pf-badge">{STATUS_LABEL_KEY[status] ? t(STATUS_LABEL_KEY[status]) : status}</span>
                  </span>
                  <span className="pf-mgmt-panel__s">
                    {[e.contact, e.requestedItem, e.note].filter(Boolean).join(' · ') || '—'}
                    {e.createdAt ? ` · ${whenLabel(e.createdAt)}` : ''}
                  </span>
                </span>
                {NEXT[status]?.length ? (
                  <div className="pf-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                    {busy === e.id && <Loader2 size={13} className="pf-spin" />}
                    {NEXT[status].map((a) => (
                      <button key={a.to} type="button" className="pf-btn pf-btn--sm" disabled={busy === e.id} onClick={() => void setStatus(e.id, a.to)}>
                        {t(a.labelKey)}
                      </button>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
